import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { UserCog } from "lucide-react";
import { useAuth } from "../../hooks/auth";
import { useToast } from "../../hooks/toast";
import api from "../../services/api";
import { updateUserRole } from "../../services/userRole";
import refreshToken from "../../utils/refreshToken";
import {
  CONSULTANT_REASSIGN_PATH,
  hasConsultantPropertiesBlocker,
  parseRoleChangeError,
  type RoleChangeBlocker,
} from "../../utils/userRoleChange";
import {
  getAssignableRolesExcluding,
  getUserRoleLabel,
  type UserRoleId,
} from "../../utils/userRoles";
import {
  FLOW_FORM_NATIVE_CONTROL_ACCENT_CLASS,
  FLOW_PRIMARY_BUTTON_BASE,
  FLOW_SECONDARY_BUTTON_BASE,
} from "../../styles/twDesignPrimitives";

type ChangeUserRoleModalProps = {
  open: boolean;
  userId: number;
  userName: string;
  currentRoleId: number;
  onClose: () => void;
  onSuccess: (roleId: number) => void;
};

export default function ChangeUserRoleModal({
  open,
  userId,
  userName,
  currentRoleId,
  onClose,
  onSuccess,
}: ChangeUserRoleModalProps) {
  const { access_token, dateToExpires } = useAuth();
  const { addToast } = useToast();

  const [selectedRoleId, setSelectedRoleId] = useState<UserRoleId>();
  const [blockers, setBlockers] = useState<RoleChangeBlocker[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;

    setSelectedRoleId(undefined);
    setBlockers([]);
    setErrorMessage("");
    setIsSubmitting(false);
  }, [open, userId]);

  const assignableRoles = getAssignableRolesExcluding(currentRoleId);
  const showReassignLink = hasConsultantPropertiesBlocker(blockers);

  async function handleSubmit() {
    if (!selectedRoleId || isSubmitting) return;

    setIsSubmitting(true);
    setBlockers([]);
    setErrorMessage("");

    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

      await updateUserRole(api, userId, selectedRoleId);

      addToast({
        title: "Papel atualizado com sucesso",
        type: "success",
      });

      onSuccess(selectedRoleId);
      onClose();
    } catch (err: unknown) {
      const parsed = parseRoleChangeError(err);
      setBlockers(parsed.blockers);
      setErrorMessage(parsed.blockers.length > 0 ? "" : parsed.fallbackMessage);

      parsed.messages.forEach((message) => {
        addToast({ title: message, type: "error" });
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      center
      classNames={{ modal: "change-user-role-modal" }}
    >
      <div className="flex w-full max-w-md flex-col">
        <div className="flex items-start gap-3">
          <UserCog className="mt-0.5 h-7 w-7 shrink-0 text-slate-700" aria-hidden />
          <div>
            <h2 className="text-lg font-bold text-slate-800">Alterar papel</h2>
            <p className="mt-1 text-sm text-slate-500">
              {userName} é {getUserRoleLabel(currentRoleId)}. Escolha o novo papel.
              A troca só é concluída se não houver pendências.
            </p>
          </div>
        </div>

        <fieldset className="mt-5 space-y-2">
          <legend className="sr-only">Novo papel</legend>
          {assignableRoles.map((roleId) => (
            <label
              key={roleId}
              className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <input
                type="radio"
                name="new_role_id"
                className={FLOW_FORM_NATIVE_CONTROL_ACCENT_CLASS}
                checked={selectedRoleId === roleId}
                onChange={() => setSelectedRoleId(roleId)}
              />
              {getUserRoleLabel(roleId)}
            </label>
          ))}
        </fieldset>

        {blockers.length > 0 && (
          <ul className="mt-4 list-disc space-y-1 pl-5 text-sm text-red-600">
            {blockers.map((blocker) => (
              <li key={`${blocker.type}-${blocker.message}`}>{blocker.message}</li>
            ))}
          </ul>
        )}

        {showReassignLink && (
          <p className="mt-3 text-sm text-slate-600">
            Transfira os imóveis pelo fluxo de{" "}
            <Link
              to={CONSULTANT_REASSIGN_PATH}
              className="font-semibold text-[#3182CE] underline"
              onClick={onClose}
            >
              troca de consultor em lote
            </Link>
            . Esta ação não faz a transferência automaticamente.
          </p>
        )}

        {!!errorMessage && (
          <p className="mt-4 text-sm text-red-600">{errorMessage}</p>
        )}

        <div className="mt-6 flex gap-3">
          <button
            type="button"
            className={`${FLOW_PRIMARY_BUTTON_BASE} flex-1 px-4 py-2 text-sm`}
            disabled={!selectedRoleId || isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting ? "Carregando..." : "Confirmar"}
          </button>
          <button
            type="button"
            className={`${FLOW_SECONDARY_BUTTON_BASE} flex-1 px-4 py-2 text-sm`}
            onClick={onClose}
          >
            Cancelar
          </button>
        </div>
      </div>
    </Modal>
  );
}
